import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard, CalendarCheck, CalendarRange, Users, Scissors, UserCog,
  DollarSign, Megaphone, Package, Clock, Settings as SettingsIcon, LogOut, Menu, X, Crown, Bell,
} from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const MENU = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/agendamentos", label: "Agendamentos", icon: CalendarCheck },
  { to: "/admin/agenda", label: "Agenda", icon: CalendarRange },
  { to: "/admin/clientes", label: "Clientes", icon: Users },
  { to: "/admin/servicos", label: "Serviços", icon: Scissors },
  { to: "/admin/barbeiros", label: "Barbeiros", icon: UserCog },
  { to: "/admin/financeiro", label: "Financeiro", icon: DollarSign },
  { to: "/admin/marketing", label: "Marketing", icon: Megaphone },
  { to: "/admin/produtos", label: "Produtos", icon: Package },
  { to: "/admin/horarios", label: "Horários", icon: Clock },
  { to: "/admin/configuracoes", label: "Configurações", icon: SettingsIcon },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [settings, setSettings] = useState({});
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState([]);
  const [unread, setUnread] = useState(0);

  const loadNotes = () => api.get("/notifications").then((r) => { setNotes(r.data.items || []); setUnread(r.data.unread || 0); }).catch(() => {});
  useEffect(() => { api.get("/settings").then((r) => setSettings(r.data || {})); loadNotes(); const t = setInterval(loadNotes, 30000); return () => clearInterval(t); }, []);
  const onNotesOpen = (o) => { if (o && unread > 0) api.post("/notifications/read-all").then(() => setUnread(0)); };

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      <div className="p-5 border-b border-zinc-800 flex items-center gap-3">
        {settings.logo ? <img src={settings.logo} alt="logo" className="h-9 w-auto object-contain" /> : <Crown className="w-7 h-7 text-amber-400" />}
        <div>
          <div className="font-display text-sm gold-text font-bold leading-tight">Big Black</div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Admin</div>
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto p-3 space-y-1">
        {MENU.map((m) => (
          <NavLink key={m.to} to={m.to} end={m.end} onClick={() => setOpen(false)}
            className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${isActive ? "bg-amber-500/15 text-amber-400 border-l-2 border-amber-400" : "text-zinc-400 hover:text-white hover:bg-zinc-800"}`}
            data-testid={`admin-nav-${m.label.toLowerCase()}`}>
            <m.icon className="w-4 h-4" /> {m.label}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-zinc-800">
        <div className="px-3 py-2 text-xs text-zinc-500 truncate">{user?.name}</div>
        <Button variant="ghost" className="w-full justify-start text-zinc-400 hover:text-red-400" onClick={() => { logout(); nav("/"); }} data-testid="admin-logout-btn">
          <LogOut className="w-4 h-4 mr-2" /> Sair
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#09090b] flex">
      <aside className="hidden lg:block w-64 border-r border-zinc-800 bg-zinc-950 fixed h-screen">
        <SidebarContent />
      </aside>

      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="w-64 bg-zinc-950 border-r border-zinc-800 h-full"><SidebarContent /></div>
          <div className="flex-1 bg-black/60" onClick={() => setOpen(false)} />
        </div>
      )}

      <div className="flex-1 lg:ml-64">
        <header className="h-16 border-b border-zinc-800 bg-zinc-950/80 backdrop-blur flex items-center justify-between px-4 sm:px-6 sticky top-0 z-40">
          <div className="flex items-center gap-3">
            <button className="lg:hidden text-zinc-300" onClick={() => setOpen(true)} data-testid="admin-menu-toggle"><Menu /></button>
            <span className="text-zinc-400 text-sm">{new Date().toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "long" })}</span>
          </div>
          <div className="flex items-center gap-4">
            <Popover onOpenChange={onNotesOpen}>
              <PopoverTrigger asChild>
                <button className="relative text-zinc-300 hover:text-amber-400 transition-colors" data-testid="notifications-bell">
                  <Bell className="w-5 h-5" />
                  {unread > 0 && <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center" data-testid="notif-badge">{unread}</span>}
                </button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-80 bg-zinc-900 border-zinc-800 p-0 max-h-96 overflow-y-auto">
                <div className="p-3 border-b border-zinc-800 text-white font-semibold text-sm">Notificações</div>
                {notes.length === 0 ? (
                  <p className="p-4 text-zinc-500 text-sm">Nenhuma notificação ainda.</p>
                ) : notes.map((n) => (
                  <div key={n.id} className="p-3 border-b border-zinc-800/60" data-testid={`notif-${n.id}`}>
                    <div className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${n.type === "cancel" ? "bg-red-500" : "bg-green-500"}`} />
                      <span className="text-sm font-medium text-white">{n.title}</span>
                    </div>
                    <p className="text-xs text-zinc-400 mt-1">{n.message}</p>
                    <p className="text-[10px] text-zinc-600 mt-1">{new Date(n.created_at).toLocaleString("pt-BR")}</p>
                  </div>
                ))}
              </PopoverContent>
            </Popover>
            <Button className="bg-gradient-to-r from-amber-400 to-yellow-600 text-black font-bold rounded-full h-9" onClick={() => nav("/admin/agendamentos")} data-testid="admin-new-appointment-btn">
              + Agendamento
            </Button>
          </div>
        </header>
        <main className="p-4 sm:p-6 lg:p-8"><Outlet /></main>
      </div>
    </div>
  );
}
